import { lazy, Suspense } from "react";
import {
	Navigate,
	Route,
	Routes,
	useLocation,
	useParams
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import GuardRoute from "src/containers/GuardRoute/GuardRoute";
import { AppLayout } from "src/components/layout/AppLayout";

import "react-toastify/dist/ReactToastify.css";

// Route-level code splitting — each page module is loaded on demand.
const EditForms = lazy(() => import("src/pages/EditForms/EditForms"));
const NewObservationForm = lazy(() =>
	import("src/features/observationsAdmin/pages/NewObservationForm/NewObservationForm").then(
		(m) => ({ default: m.NewObservationForm })
	)
);
const EditObservationForm = lazy(() =>
	import("src/pages/EditObservationForm/EditObservationForm").then((m) => ({
		default: m.EditObservationForm
	}))
);
const ReportDetail = lazy(() => import("src/pages/ReportDetail/ReportDetail"));
const Reports = lazy(() => import("src/pages/Reports/Reports"));

const EditObservationQuestions = lazy(
	() => import("src/pages/EditObservationQuestions/EditObservationQuestions")
);
const Locations = lazy(
	() => import("src/features/Locations/pages/Locations/Locations")
);
const CreateRegion = lazy(
	() => import("src/features/Locations/pages/CreateRegion/CreateRegion")
);
const EditRegion = lazy(
	() => import("src/features/Locations/pages/EditRegion/EditRegion")
);
const CreateArea = lazy(
	() => import("src/features/Locations/pages/CreateArea/CreateArea")
);
const EditArea = lazy(
	() => import("src/features/Locations/pages/EditArea/EditArea")
);
const CreateStore = lazy(
	() => import("src/features/Locations/pages/CreateStore/CreateStore")
);
const EditStore = lazy(
	() => import("src/features/Locations/pages/EditStore/EditStore")
);

const Home = lazy(() => import("src/pages/Home/Home"));
const CreateCarouselBanner = lazy(
	() =>
		import("src/features/HomepageCarouselBanners/pages/CreateCarouselBanner/CreateCarouselBanner")
);
const HomepageCarousel = lazy(
	() => import("src/pages/Banners/HomepageCarousel")
);
const CreateFullScreenTakeover = lazy(
	() =>
		import("src/features/FullScreenTakeover/pages/CreateFullScreenTakeover/CreateFullScreenTakeover")
);
const FullScreenTakeover = lazy(
	() =>
		import("src/features/FullScreenTakeover/pages/FullScreenTakeover/FullScreenTakeover")
);
const EditCarouselBanner = lazy(
	() =>
		import("src/features/HomepageCarouselBanners/pages/EditCarouselBanner/EditCarouselBanner")
);
const EditFullScreenTakeover = lazy(
	() =>
		import("src/features/FullScreenTakeover/pages/EditFullScreenTakeover/EditFullScreenTakeover")
);
const CreatePromotion = lazy(
	() => import("src/features/Promotion/pages/CreatePromotion/CreatePromotion")
);
const EditPromotion = lazy(
	() => import("src/features/Promotion/pages/EditPromotion/EditPromotion")
);
const Promotion = lazy(
	() => import("src/features/Promotion/pages/Promotion/Promotion")
);
const AppBranding = lazy(
	() => import("src/features/AppBranding/pages/AppBranding/AppBranding")
);
const Analytics = lazy(
	() => import("src/features/Analytics/pages/Analytics/Analytics")
);
const PersonProfile = lazy(
	() => import("src/features/Analytics/pages/PersonProfile/PersonProfile")
);
const CreateAppBranding = lazy(
	() =>
		import("src/features/AppBranding/pages/CreateAppBranding/CreateAppBranding")
);
const EditAppBranding = lazy(
	() => import("src/features/AppBranding/pages/EditAppBranding/EditAppBranding")
);
const QuickLinks = lazy(
	() => import("src/features/QuickLinks/pages/QuickLinks/QuickLinks")
);
const CreateQuickLink = lazy(
	() => import("src/features/QuickLinks/pages/CreateQuickLink/CreateQuickLink")
);
const EditQuickLink = lazy(
	() => import("src/features/QuickLinks/pages/EditQuickLink/EditQuickLink")
);
const HomepageOrdering = lazy(
	() =>
		import("src/features/HomepageOrdering/pages/HomepageOrdering/HomepageOrdering")
);
const SectionTitles = lazy(
	() => import("src/features/SectionTitles/pages/SectionTitles/SectionTitles")
);
const Navigation = lazy(
	() => import("src/features/Navigation/pages/Navigation/Navigation")
);
const KeyShouts = lazy(
	() => import("src/features/KeyShouts/pages/KeyShouts/KeyShouts")
);
const CreateKeyShout = lazy(
	() => import("src/features/KeyShouts/pages/CreateKeyShout/CreateKeyShout")
);
const EditKeyShout = lazy(
	() => import("src/features/KeyShouts/pages/EditKeyShout/EditKeyShout")
);
const Announcements = lazy(
	() => import("src/features/Announcements/pages/Announcements/Announcements")
);
const CreateAnnouncement = lazy(
	() =>
		import("src/features/Announcements/pages/CreateAnnouncement/CreateAnnouncement")
);
const EditAnnouncement = lazy(
	() =>
		import("src/features/Announcements/pages/EditAnnouncement/EditAnnouncement")
);
const PagesList = lazy(() => import("src/features/Pages/pages/PagesList"));
const CreatePage = lazy(() => import("src/features/Pages/pages/CreatePage"));
const EditPage = lazy(() => import("src/features/Pages/pages/EditPage"));
const NewsAdminList = lazy(() => import("src/features/News/pages/NewsList"));
const CreateNews = lazy(() => import("src/features/News/pages/CreateNews"));
const EditNews = lazy(() => import("src/features/News/pages/EditNews"));
const PageView = lazy(() =>
	import("src/features/Pages/pages/PageView").then((m) => ({
		default: m.PageView
	}))
);
const Events = lazy(() => import("src/features/Events/pages/Events/Events"));
const CreateEvent = lazy(
	() => import("src/features/Events/pages/CreateEvent/CreateEvent")
);
const EditEvent = lazy(
	() => import("src/features/Events/pages/EditEvent/EditEvent")
);
const AdminHashtags = lazy(
	() => import("src/features/Hashtags/pages/Hashtags/Hashtags")
);
const CreateHashtag = lazy(
	() => import("src/features/Hashtags/pages/CreateHashtag/CreateHashtag")
);
const EditHashtag = lazy(
	() => import("src/features/Hashtags/pages/EditHashtag/EditHashtag")
);
const Recognitions = lazy(
	() => import("src/features/Recognition/pages/Recognitions/Recognitions")
);
const RecognitionTypes = lazy(
	() =>
		import("src/features/Recognition/pages/RecognitionTypes/RecognitionTypes")
);
const CreateRecognitionType = lazy(
	() =>
		import("src/features/Recognition/pages/CreateRecognitionType/CreateRecognitionType")
);
const EditRecognitionType = lazy(
	() =>
		import("src/features/Recognition/pages/EditRecognitionType/EditRecognitionType")
);
const RecognitionSettings = lazy(
	() =>
		import("src/features/Recognition/pages/RecognitionSettings/RecognitionSettings")
);
const Groups = lazy(() => import("src/features/Groups/pages/Groups/Groups"));
const CreateGroup = lazy(
	() => import("src/features/Groups/pages/CreateGroup/CreateGroup")
);
const EditGroup = lazy(
	() => import("src/features/Groups/pages/EditGroup/EditGroup")
);
const People = lazy(() => import("src/features/People/pages/People/People"));
const PersonDetail = lazy(
	() => import("src/features/People/pages/PersonDetail/PersonDetail")
);
const Permissions = lazy(
	() => import("src/features/Permissions/pages/Permissions/Permissions")
);
const CreateRole = lazy(
	() => import("src/features/Permissions/pages/CreateRole/CreateRole")
);
const EditRole = lazy(
	() => import("src/features/Permissions/pages/EditRole/EditRole")
);
const FavouriteFeeds = lazy(
	() =>
		import("src/features/FavouriteFeeds/pages/FavouriteFeeds/FavouriteFeeds")
);
const AccessDenied = lazy(() => import("src/pages/AccessDenied/AccessDenied"));
const EstateReportDetail = lazy(
	() => import("src/pages/EstateReportDetail/EstateReportDetail")
);
const ProtectedImageView = lazy(
	() =>
		import("src/features/observationsAdmin/pages/ProtectedImageView/ProtectedImageView")
);
const SocialFeedContent = lazy(
	() =>
		import("src/features/SocialFeedContent/pages/SocialFeedContent/SocialFeedContent")
);
// Ported consumer-web social feed (renders exactly like mee.web): post cards,
// MediaStrip carousel, video, fullscreen detail + lightbox, reactions, comments.
// Now mounted UNDER the consumer shell (AppShell) at /social.
const SocialFeed = lazy(() =>
	import("src/social/SocialFeed").then((m) => ({ default: m.SocialFeed }))
);
const SocialPostDetail = lazy(() =>
	import("src/social/SocialPostDetail").then((m) => ({
		default: m.SocialPostDetail
	}))
);

// Consumer-web shell (ported from mee.web): TopNav + LeftSidebar (CMS side menu)
// + BottomTabBar wrapping an <Outlet/>. Reuses admin's MSAL via the auth seam
// (src/auth/useAuth) and the apiClient shim — no second MSAL instance.
const AppShell = lazy(() =>
	import("src/shell/AppShell").then((m) => ({ default: m.AppShell }))
);
// Ported consumer-web homepage (carousel, events, key shouts, quick links,
// promotions, favourite feeds, full-screen takeover). Mounted at "/" under the
// consumer AppShell, reusing admin auth via the apiClient shim — no second MSAL.
const HomePage = lazy(() =>
	import("src/home/HomePage").then((m) => ({ default: m.HomePage }))
);
const SearchPage = lazy(() =>
	import("src/search/SearchPage").then((m) => ({ default: m.SearchPage }))
);
// Ported consumer-web News (list of article tiles + article detail) and
// Notifications (grouped list, announcement preview/modal, deep-links). Mounted
// under the consumer AppShell, reusing admin auth via the apiClient shim.
const NewsList = lazy(() =>
	import("src/news/NewsList").then((m) => ({ default: m.NewsList }))
);
const NewsArticle = lazy(() =>
	import("src/news/NewsArticle").then((m) => ({ default: m.NewsArticle }))
);
const NotificationsList = lazy(() =>
	import("src/notifications/NotificationsList").then((m) => ({
		default: m.NotificationsList
	}))
);
const ProfilePage = lazy(() =>
	import("src/profile/ProfilePage").then((m) => ({ default: m.ProfilePage }))
);
const UserProfilePage = lazy(() =>
	import("src/profile/UserProfilePage").then((m) => ({
		default: m.UserProfilePage
	}))
);
// Ported consumer-web Ops / OBSERVATIONS area (renders exactly like mee.web):
// the category list (/ops), a category's forms (/ops/category/:name), the
// form-detail + subject-selection page (/ops/form/:id) and the dynamic
// fill/submit page (/ops/:id). Mounted under the consumer AppShell, reusing
// admin auth via the apiClient shim — no second MSAL.
const ObservationsPage = lazy(
	() => import("src/observations/ObservationsPage")
);
const ObservationCategoryPage = lazy(
	() => import("src/observations/ObservationCategoryPage")
);
const ObservationFormDetailPage = lazy(
	() => import("src/observations/ObservationFormDetailPage")
);
const ObservationFormPage = lazy(
	() => import("src/observations/ObservationFormPage")
);
const Error404 = lazy(() => import("src/pages/Error404/Error404"));

/**
 * The admin "Observations" section was renamed "Operations" (label + browser
 * URLs only — the backend API and internal code names are unchanged). Old
 * /admin/observations/* bookmarks and deep links redirect to the equivalent
 * /admin/operations/* URL, preserving the rest of the path and query string.
 */
export const LegacyObservationsRedirect = () => {
	const { pathname, search } = useLocation();

	return (
		<Navigate
			to={pathname.replace("/admin/observations", "/admin/operations") + search}
			replace
		/>
	);
};

/**
 * A direct/pasted `/events/:id` URL redirects to `/?event={id}`, which opens
 * the shared EventDetailModal in place (via AppShell's EventModalOverlay)
 * instead of landing on the old standalone EventDetailPage (removed — the
 * rejected full-page layout). Landing on "/" rather than preserving whatever
 * path the id came from is deliberate: unlike LegacyObservationsRedirect
 * (which rewrites one segment of an otherwise-valid path), `/events/:id` on
 * its own carries no other path context worth keeping.
 */
export const EventRouteRedirect = () => {
	const { id } = useParams<{ id: string }>();

	return <Navigate to={`/?event=${id}`} replace />;
};

const AppTemplate = () => {
	return (
		<>
			<ToastContainer
				position="top-right"
				autoClose={5000}
				hideProgressBar={false}
				newestOnTop={true}
				closeOnClick={true}
				rtl={false}
				pauseOnFocusLoss
				draggable
				pauseOnHover
				theme="light"
			/>

			<Suspense fallback={null}>
				<Routes>
					{/* CONSUMER app — the ported mee.web shell (TopNav + CMS side
					    menu + bottom tab bar) wraps every consumer route via its
					    <Outlet/>. Root "/" is now the consumer experience (admin
					    lives under /admin). */}
					<Route element={<AppShell />}>
						<Route path="/" element={<HomePage />} />

						{/* Ported social feed — renders exactly like web (post cards,
						    AssetImage, MediaStrip, video, reactions, comments), now
						    inside the consumer shell. */}
						<Route path="/social" element={<SocialFeed />} />
						<Route path="/social/feed/:hashtag" element={<SocialFeed />} />
						<Route path="/social/:id" element={<SocialPostDetail />} />

						{/* Search + profile pages (ported; hooks already in place). */}
						<Route path="/search" element={<SearchPage />} />
						<Route path="/profile" element={<ProfilePage />} />
						<Route path="/profile/:id" element={<UserProfilePage />} />

						{/* Ported News (list + article detail) and Notifications
						    (grouped list + announcement modal), inside the consumer
						    shell. */}
						<Route path="/news" element={<NewsList />} />
						<Route path="/news/:slug" element={<NewsArticle />} />
						<Route path="/notifications" element={<NotificationsList />} />

						{/* A direct/pasted /events/:id URL redirects to the in-place
						    modal (?event={id}) rather than a standalone page — every
						    Events link (carousel/keyshout/quicklink/news, etc.) already
						    emits ?event={id} via resolveContentLink; this route exists
						    only to catch bookmarks/typed URLs in the old shape. */}
						<Route path="/events/:id" element={<EventRouteRedirect />} />

						{/* Ported Ops / OBSERVATIONS — category list, a category's
						    forms, the form-detail + subject-selection page, and the
						    dynamic fill/submit page. Routes match mee.web exactly so
						    the feed's SubmissionLinkCard links (/ops/form/:id,
						    /ops/:id) resolve once that card is ported. */}
						<Route path="/ops" element={<ObservationsPage />} />
						<Route
							path="/ops/category/:name"
							element={<ObservationCategoryPage />}
						/>
						<Route
							path="/ops/form/:id"
							element={<ObservationFormDetailPage />}
						/>
						<Route path="/ops/:id" element={<ObservationFormPage />} />

						{/* Published pages — consumer pretty URL (/pages/:slug). */}
						<Route path="/pages/:slug" element={<PageView />} />
					</Route>

					{/* ProtectedImageView bypasses layout (WebView only route) */}
					<Route element={<GuardRoute restrictedToEstateManager={true} />}>
						<Route
							path="/admin/operations/image/:imageId"
							element={<ProtectedImageView />}
						/>
					</Route>

					{/* Legacy URLs — the "Observations" section is now "Operations";
					    old bookmarks/deep links redirect to the new path. */}
					<Route
						path="/admin/observations/*"
						element={<LegacyObservationsRedirect />}
					/>

					{/* All other routes wrapped in AppLayout */}
					<Route element={<AppLayout />}>
						<Route path="/admin" element={<Home />} />

						<Route path="/admin/access-denied" element={<AccessDenied />} />

						{/* Protected Routes */}
						<Route element={<GuardRoute restrictedToEstateManager={true} />}>
							<Route
								path="/admin/operations/reports/estate/:formId"
								element={<EstateReportDetail />}
							/>
						</Route>

						<Route
							element={
								<GuardRoute requiredPermission="CanViewObservationReporting" />
							}
						>
							<Route path="/admin/operations/reports" element={<Reports />} />

							<Route
								path="/admin/operations/reports/form/:formId"
								element={<ReportDetail />}
							/>
							<Route
								path="/admin/operations/reports/form/:formId/region/:selectedRegion"
								element={<ReportDetail />}
							/>
							<Route
								path="/admin/operations/reports/form/:formId/region/:selectedRegion/area/:selectedArea"
								element={<ReportDetail />}
							/>
							<Route
								path="/admin/operations/reports/form/:formId/region/:selectedRegion/area/:selectedArea/store/:selectedStore"
								element={<ReportDetail />}
							/>
						</Route>

						<Route
							element={
								<GuardRoute requiredPermission="CanViewEditObservationForms" />
							}
						>
							<Route path="/admin/operations/forms" element={<EditForms />} />

							<Route
								path="/admin/operations/forms/:formId/settings/"
								element={<EditObservationForm />}
							/>

							<Route
								path="/admin/operations/forms/:formId/questions"
								element={<EditObservationQuestions />}
							/>

							<Route
								path="/admin/operations/forms/new-observation-form"
								element={<NewObservationForm />}
							/>
						</Route>

						<Route
							element={<GuardRoute requiredPermission="CanViewSocialPosts" />}
						>
							<Route
								path="/admin/social-feed"
								element={<SocialFeedContent />}
							/>
						</Route>

						<Route
							element={<GuardRoute requiredPermission="CanViewAnalytics" />}
						>
							<Route path="/admin/analytics" element={<Analytics />} />
						</Route>

						{/* Person-centric drill-down — gated on the tighter
						    CanViewUserAnalytics directly, matching
						    /admin/users/people/:personId below. */}
						<Route
							element={<GuardRoute requiredPermission="CanViewUserAnalytics" />}
						>
							<Route
								path="/admin/analytics/people/:userId"
								element={<PersonProfile />}
							/>
						</Route>

						{/* Homepage Content (carousel, takeover, promotion, quick links, key shouts, ordering, branding) */}
						<Route
							element={
								<GuardRoute requiredPermission="CanViewEditHomepageContent" />
							}
						>
							<Route
								path="/admin/vendor-sponsorship"
								element={
									<Navigate
										to="/vendor-sponsorship/homepage-carousel/banners?published=true"
										replace
									/>
								}
							/>
							<Route
								path="/admin/vendor-sponsorship/homepage-carousel/banners/new-carousel-banner-form"
								element={<CreateCarouselBanner />}
							/>
							<Route
								path="/admin/vendor-sponsorship/homepage-carousel/banners/new-carousel-banner-form/:formId"
								element={<EditCarouselBanner />}
							/>
							<Route
								path="/admin/vendor-sponsorship/homepage-carousel/banners"
								element={<HomepageCarousel />}
							/>
							<Route
								path="/admin/vendor-sponsorship/full-screen-takeover/banners"
								element={<FullScreenTakeover />}
							/>
							<Route
								path="/admin/vendor-sponsorship/full-screen-takeover/banners/new-full-page-takeover-form"
								element={<CreateFullScreenTakeover />}
							/>
							<Route
								path="/admin/vendor-sponsorship/full-screen-takeover/banners/new-full-page-takeover-form/:formId"
								element={<EditFullScreenTakeover />}
							/>
							<Route
								path="/admin/vendor-sponsorship/promotion/banners"
								element={<Promotion />}
							/>
							<Route
								path="/admin/vendor-sponsorship/promotion/banners/new-promotion-form"
								element={<CreatePromotion />}
							/>
							<Route
								path="/admin/vendor-sponsorship/promotion/banners/new-promotion-form/:formId"
								element={<EditPromotion />}
							/>
							<Route
								path="/admin/app/homepage/quick-links"
								element={<QuickLinks />}
							/>
							<Route
								path="/admin/app/homepage/quick-links/create"
								element={<CreateQuickLink />}
							/>
							<Route
								path="/admin/app/homepage/quick-links/:quickLinkId"
								element={<EditQuickLink />}
							/>
							<Route
								path="/admin/app/homepage/ordering"
								element={<HomepageOrdering />}
							/>
							<Route
								path="/admin/app/homepage/key-shouts"
								element={<KeyShouts />}
							/>
							<Route
								path="/admin/app/homepage/key-shouts/create"
								element={<CreateKeyShout />}
							/>
							<Route
								path="/admin/app/homepage/key-shouts/:keyShoutId"
								element={<EditKeyShout />}
							/>
							<Route
								path="/admin/vendor-sponsorship/app-branding/brands"
								element={<AppBranding />}
							/>
							<Route
								path="/admin/vendor-sponsorship/app-branding/brands/new-app-branding-form"
								element={<CreateAppBranding />}
							/>
							<Route
								path="/admin/vendor-sponsorship/app-branding/brands/new-app-branding-form/:brandId"
								element={<EditAppBranding />}
							/>
						</Route>

						{/* Announcements */}
						<Route
							element={
								<GuardRoute requiredPermission="CanManageAnnouncements" />
							}
						>
							<Route path="/admin/announcements" element={<Announcements />} />
							<Route
								path="/admin/announcements/create"
								element={<CreateAnnouncement />}
							/>
							<Route
								path="/admin/announcements/:announcementId"
								element={<EditAnnouncement />}
							/>
						</Route>

						{/* Pages */}
						<Route element={<GuardRoute requiredPermission="CanManagePages" />}>
							<Route path="/admin/app/pages" element={<PagesList />} />
							<Route path="/admin/app/pages/create" element={<CreatePage />} />
							<Route path="/admin/app/pages/:pageId" element={<EditPage />} />
						</Route>

						{/* News */}
						<Route element={<GuardRoute requiredPermission="CanManageNews" />}>
							<Route path="/admin/app/news" element={<NewsAdminList />} />
							<Route path="/admin/app/news/create" element={<CreateNews />} />
							<Route path="/admin/app/news/:newsId" element={<EditNews />} />
						</Route>

						{/* Events */}
						<Route
							element={<GuardRoute requiredPermission="CanViewEditEvents" />}
						>
							<Route path="/admin/app/events" element={<Events />} />
							<Route
								path="/admin/app/events/create"
								element={<CreateEvent />}
							/>
							<Route
								path="/admin/app/events/:eventId"
								element={<EditEvent />}
							/>
						</Route>

						{/* Hashtags */}
						<Route
							element={<GuardRoute requiredPermission="CanManageHashtags" />}
						>
							<Route
								path="/admin/social-feed/hashtags"
								element={<AdminHashtags />}
							/>
							<Route
								path="/admin/social-feed/hashtags/create"
								element={<CreateHashtag />}
							/>
							<Route
								path="/admin/social-feed/hashtags/:hashtagId"
								element={<EditHashtag />}
							/>
						</Route>

						{/* Recognition — data list, Types CRUD, and Messaging settings. */}
						<Route
							element={<GuardRoute requiredPermission="CanManageRecognition" />}
						>
							<Route path="/admin/recognition" element={<Recognitions />} />
							<Route
								path="/admin/recognition/types"
								element={<RecognitionTypes />}
							/>
							<Route
								path="/admin/recognition/types/create"
								element={<CreateRecognitionType />}
							/>
							<Route
								path="/admin/recognition/types/:recognitionTypeId"
								element={<EditRecognitionType />}
							/>
							<Route
								path="/admin/recognition/settings"
								element={<RecognitionSettings />}
							/>
							{/* The page was "Messaging" before it gained the enable
							    toggle — keep existing bookmarks and links working. */}
							<Route
								path="/admin/recognition/messaging"
								element={<Navigate to="/admin/recognition/settings" replace />}
							/>
						</Route>

						{/* Favourite Feeds */}
						<Route
							element={<GuardRoute requiredPermission="CanManageHashtags" />}
						>
							<Route
								path="/admin/social-feed/favourite-feeds"
								element={<FavouriteFeeds />}
							/>
						</Route>

						{/* Section Titles */}
						<Route
							element={<GuardRoute requiredPermission="CanManageSettings" />}
						>
							<Route
								path="/admin/app/homepage/section-titles"
								element={<SectionTitles />}
							/>
						</Route>

						{/* Navigation */}
						<Route
							element={
								<GuardRoute requiredPermission="CanViewEditNavigation" />
							}
						>
							<Route path="/admin/app/navigation" element={<Navigation />} />
						</Route>

						{/* People */}
						<Route
							element={<GuardRoute requiredPermission="CanViewEditPeople" />}
						>
							<Route path="/admin/users/people" element={<People />} />
							<Route
								path="/admin/users/people/:personId"
								element={<PersonDetail />}
							/>
						</Route>

						{/* Permissions */}
						<Route
							element={<GuardRoute requiredPermission="CanViewEditUserRoles" />}
						>
							<Route
								path="/admin/users/permissions"
								element={<Permissions />}
							/>
							<Route
								path="/admin/users/permissions/create"
								element={<CreateRole />}
							/>
							<Route
								path="/admin/users/permissions/:roleId"
								element={<EditRole />}
							/>
						</Route>

						{/* Groups */}
						<Route
							element={<GuardRoute requiredPermission="CanViewEditGroups" />}
						>
							<Route path="/admin/users/groups" element={<Groups />} />
							<Route
								path="/admin/users/groups/create"
								element={<CreateGroup />}
							/>
							<Route
								path="/admin/users/groups/:groupId"
								element={<EditGroup />}
							/>
						</Route>

						{/* Locations */}
						<Route
							element={<GuardRoute requiredPermission="CanViewEditLocations" />}
						>
							<Route path="/admin/users/locations" element={<Locations />} />
							<Route
								path="/admin/users/locations/regions/create"
								element={<CreateRegion />}
							/>
							<Route
								path="/admin/users/locations/regions/:regionId"
								element={<EditRegion />}
							/>
							<Route
								path="/admin/users/locations/areas/create"
								element={<CreateArea />}
							/>
							<Route
								path="/admin/users/locations/areas/:areaId"
								element={<EditArea />}
							/>
							<Route
								path="/admin/users/locations/stores/create"
								element={<CreateStore />}
							/>
							<Route
								path="/admin/users/locations/stores/:storeId"
								element={<EditStore />}
							/>
						</Route>

						<Route path="*" element={<Error404 />} />
					</Route>
				</Routes>
			</Suspense>
		</>
	);
};

export default AppTemplate;
